import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { entities as entitiesApi, media } from "../services/api";
import { useApp } from "../context/AppContext";
import { formatDate } from "../utils/dateUtils";
import {
  BackIcon,
  PlusIcon,
  ProductIcon,
  CloseIcon,
  MergeIcon,
  TrashIcon,
  DuplicateIcon,
  UploadIcon,
  DownloadIcon,
} from "../components/Icons";
import { ConfirmModal } from "../components/Modal";
import { toast } from "../components/Toast";
import DataTable from "../components/DataTable";
import DuplicateScannerModal from "../components/DuplicateScannerModal";
import VineImportModal from "../components/VineImportModal";
import EbayListingModal from "../components/EbayListingModal";
import OfferUpListingModal from "../components/OfferUpListingModal";
import FacebookListingModal from "../components/FacebookListingModal";
import {
  downloadFile,
  productsToCsv,
  parseReviewImportCsv,
} from "../utils/exportUtils";
import { LOW_DATA_MODE } from "../lowData";

function Products() {
  const navigate = useNavigate();
  const {
    products,
    productsLoading,
    productsLoadingMore,
    productsHasMore,
    productsError,
    refreshProducts,
    loadMoreProducts,
    deleteEntities,
  } = useApp();
  // View state lives in the URL so navigating to a product and pressing back
  // restores the list exactly as it was. Written with replace so sorting and
  // typing don't each add a history entry the user has to click through.
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = searchParams.get("view") || "table";
  const sortField = searchParams.get("sort") || "created_at";
  const sortDirection = searchParams.get("dir") || "desc";
  const searchQuery = searchParams.get("q") || "";
  const listedFilter = searchParams.get("listed") || "";
  // Review filters in the URL: revs/myrev are "" (off), "1" (has content),
  // or "0" (missing it). The UI exposes them as task-oriented presets.
  const reviewsFilter = searchParams.get("revs") || "";
  const myReviewFilter = searchParams.get("myrev") || "";
  const vinedFilter = searchParams.get("vined") || "";
  // Kanban drill-down ("<pipeline>.<column>"), set by the board's View all links
  const boardFilter = searchParams.get("board") || "";
  const REVIEW_PRESETS = {
    needs: { revs: "1", myrev: "0", vined: "" }, // generation queue
    mine: { revs: "", myrev: "1", vined: "" },
    vineready: { revs: "", myrev: "1", vined: "0" }, // written, not yet on Vine
    vinedone: { revs: "", myrev: "", vined: "1" },
    none: { revs: "0", myrev: "", vined: "" },
  };
  const reviewPreset =
    Object.keys(REVIEW_PRESETS).find(
      (k) =>
        REVIEW_PRESETS[k].revs === reviewsFilter &&
        REVIEW_PRESETS[k].myrev === myReviewFilter &&
        REVIEW_PRESETS[k].vined === vinedFilter,
    ) || "";

  const updateParams = useCallback(
    (updates) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          });
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [selectedIds, setSelectedIds] = useState([]);
  // Open Actions dropdown: which row, and where to anchor the floating menu.
  // position: fixed keeps it out of the table layout entirely, so opening it
  // never resizes the column or shifts other rows.
  const [actionsOpen, setActionsOpen] = useState(null); // {id, x, y}
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showVineImportModal, setShowVineImportModal] = useState(false);
  const [offerupProduct, setOfferupProduct] = useState(null);
  const [facebookProduct, setFacebookProduct] = useState(null);
  const [ebayProduct, setEbayProduct] = useState(null);
  const [merging, setMerging] = useState(false);
  const [stats, setStats] = useState({
    totalCount: 0,
    inContainers: 0,
    totalValue: 0,
  });
  // Restore scroll position on return. Keyed to the view so a different sort or
  // search starts at the top rather than at a stale offset.
  const scrollKey = `products:scroll:${sortField}:${sortDirection}:${searchQuery}:${listedFilter}:${reviewsFilter}:${myReviewFilter}:${vinedFilter}:${boardFilter}:${viewMode}`;
  const scrollRestoredRef = useRef(false);

  useEffect(() => {
    const save = () => {
      sessionStorage.setItem(scrollKey, String(window.scrollY));
    };
    window.addEventListener("scroll", save, { passive: true });
    return () => {
      save();
      window.removeEventListener("scroll", save);
    };
  }, [scrollKey]);

  // Wait for the first page of results — restoring before the rows exist would
  // clamp the scroll back to zero.
  useEffect(() => {
    if (scrollRestoredRef.current || productsLoading) return;
    if (!products || products.length === 0) return;

    const saved = parseInt(sessionStorage.getItem(scrollKey) || "0", 10);
    scrollRestoredRef.current = true;
    if (saved > 0) {
      requestAnimationFrame(() => window.scrollTo(0, saved));
    }
  }, [productsLoading, products, scrollKey]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await entitiesApi.stats("product");
      setStats(data);
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  }, []);

  useEffect(() => {
    // Any change to the result set invalidates the selection. Clearing here
    // (rather than in each filter handler) covers every path that changes the
    // URL — search, sort, the listed/review dropdowns, board drill-down links,
    // and back/forward — so a hidden selection can't survive into a different
    // result set where bulk export/delete would hit rows the user can't see.
    setSelectedIds([]);
    refreshProducts({
      sort_by: sortField,
      sort_order: sortDirection,
      search: searchQuery || null,
      listed_on: listedFilter || null,
      review_filters: {
        has_reviews: reviewsFilter,
        has_my_review: myReviewFilter,
        vined: vinedFilter,
      },
      board: boardFilter || null,
    });
  }, [sortField, sortDirection, searchQuery, listedFilter, reviewsFilter, myReviewFilter, vinedFilter, boardFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stats don't change with sorting/filtering — load once per visit instead
  // of on every filter change (each stats call is a full-table D1 scan).
  useEffect(() => {
    if (LOW_DATA_MODE) return; // cards hidden, skip the scans
    loadStats();
  }, [loadStats]);

  // DataTable debounces before calling this; the fetch is driven by the effect
  // above reacting to the URL change.
  const handleSearchChange = useCallback(
    (value) => {
      updateParams({ q: value });
    },
    [updateParams],
  );

  const handleSort = (field, direction) => {
    updateParams({ sort: field, dir: direction });
  };

  const handleViewModeChange = (mode) => {
    updateParams({ view: mode === "table" ? "" : mode });
  };

  const handleSelectAllAcrossPages = async () => {
    try {
      const { data } = await entitiesApi.list({
        entity_type: "product",
        limit: -1,
        ...(searchQuery ? { search: searchQuery } : {}),
        ...(listedFilter ? { listed_on: listedFilter } : {}),
        ...(reviewsFilter ? { has_reviews: reviewsFilter } : {}),
        ...(myReviewFilter ? { has_my_review: myReviewFilter } : {}),
        ...(vinedFilter ? { vined: vinedFilter } : {}),
        ...(boardFilter ? { board: boardFilter } : {}),
      });
      setSelectedIds(data.map((p) => p.id));
    } catch (err) {
      console.error("Failed to select all:", err);
      toast.error("Couldn't select all products");
    }
  };

  const handleBulkDelete = async () => {
    try {
      await deleteEntities(selectedIds, "product");
      setSelectedIds([]);
      setShowDeleteConfirm(false);
      loadStats();
    } catch (err) {
      console.error("Failed to delete:", err);
      toast.error(
        err.response?.data?.error ||
          err.message ||
          "Failed to delete some items",
      );
    }
  };

  const handleMerge = async (primaryId) => {
    setMerging(true);
    try {
      const idsToMerge = selectedIds.filter((id) => id !== primaryId);
      await entitiesApi.merge(primaryId, idsToMerge);
      setSelectedIds([]);
      setShowMergeModal(false);
      refreshProducts();
      loadStats();
    } catch (err) {
      console.error("Failed to merge:", err);
      toast.error(err.response?.data?.error || "Failed to merge items");
    } finally {
      setMerging(false);
    }
  };

  const getSelectedProducts = () => {
    return (products || []).filter((p) => selectedIds.includes(p.id));
  };

  // Round-trip of the CSV export: rows carry the entity Id, plus the review
  // columns filled in externally (e.g. by a review generator).
  const reviewImportInputRef = useRef(null);

  const handleImportReviewsFile = async (file) => {
    if (!file) return;
    try {
      const updates = parseReviewImportCsv(await file.text());
      if (!updates.length) {
        toast.error("No rows with review content found");
        return;
      }
      const applyUpdate = async (u) => {
        try {
          const body = {};
          if (u.my_review) body.my_review = u.my_review;
          if (u.my_rating) body.my_rating = u.my_rating;
          await entitiesApi.update(u.id, body);
          return true;
        } catch (err) {
          console.error("Review import failed for", u.id, err);
          return false;
        }
      };
      let ok = 0;
      let failed = 0;
      for (let i = 0; i < updates.length; i += 10) {
        const results = await Promise.all(
          updates.slice(i, i + 10).map(applyUpdate),
        );
        ok += results.filter(Boolean).length;
        failed += results.filter((r) => !r).length;
      }
      toast.success(
        `Reviews imported: ${ok} updated${failed ? `, ${failed} failed` : ""}`,
      );
      refreshProducts();
    } catch (err) {
      console.error("Failed to import reviews CSV:", err);
      toast.error(err.message || "Failed to import reviews CSV");
    }
  };

  // Best-effort "review submitted to Vine" marker: opening the Vine review
  // form stamps the product; clear it from the product's edit form if the
  // review didn't actually go through.
  const markVineReviewed = async (product) => {
    if (product.vine_reviewed_at) return;
    try {
      await entitiesApi.update(product.id, {
        vine_reviewed_at: new Date().toISOString(),
      });
      refreshProducts();
    } catch (err) {
      console.error("Failed to mark Vine review:", err);
    }
  };

  // The list rows don't carry reviews, so pull each selected product's full
  // record before building the CSV. Chunked to keep large selections polite.
  const [exporting, setExporting] = useState(false);
  const handleBulkExportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const details = [];
      let failed = 0;
      for (let i = 0; i < selectedIds.length; i += 10) {
        // One row that vanished (deleted in another tab) shouldn't sink the
        // whole export — keep what resolved and report the rest.
        const results = await Promise.allSettled(
          selectedIds
            .slice(i, i + 10)
            .map((id) => entitiesApi.get(id).then((r) => r.data)),
        );
        for (const r of results) {
          if (r.status === "fulfilled") details.push(r.value);
          else failed++;
        }
      }
      if (details.length === 0) throw new Error("No products could be loaded");
      downloadFile(
        `products-export-${new Date().toISOString().slice(0, 10)}.csv`,
        productsToCsv(details),
        "text/csv",
      );
      const msg = `Exported ${details.length} product${details.length === 1 ? "" : "s"} to CSV`;
      if (failed) toast.warning(`${msg} (${failed} couldn't be loaded)`);
      else toast.success(msg);
    } catch (err) {
      console.error("Failed to export CSV:", err);
      toast.error("Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  // Column definitions
  const columns = [
    {
      field: "image",
      header: "",
      width: "50px",
      sortable: false,
      render: (_, product) => (
        <div
          onClick={() => navigate(`/entity/${product.id}`)}
          style={{ cursor: "pointer" }}
        >
          {product.image_path ? (
            <img
              src={media.getImageUrl(product.image_path)}
              alt=""
              style={{
                width: "40px",
                height: "40px",
                objectFit: "cover",
                borderRadius: "var(--radius-sm)",
                background: "var(--bg-secondary)",
              }}
            />
          ) : (
            <div
              style={{
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "var(--bg-secondary)",
                borderRadius: "var(--radius-sm)",
                color: "var(--text-muted)",
              }}
            >
              <ProductIcon size={20} />
            </div>
          )}
        </div>
      ),
    },
    {
      field: "name",
      header: "Name",
      render: (_, product) => (
        <div>
          <span
            onClick={() => navigate(`/entity/${product.id}`)}
            style={{
              fontWeight: 500,
              color: "var(--accent-primary)",
              cursor: "pointer",
            }}
          >
            {product.name}
          </span>
          {product.tags && product.tags.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "4px",
                marginTop: "4px",
              }}
            >
              {product.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag.id}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "2px 6px",
                    borderRadius: "var(--radius-sm)",
                    background: `${tag.color}20`,
                    fontSize: "0.7rem",
                  }}
                >
                  <span
                    style={{
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      background: tag.color,
                    }}
                  />
                  {tag.name}
                </span>
              ))}
              {product.tags.length > 3 && (
                <span
                  style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}
                >
                  +{product.tags.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      ),
      getSortValue: (item) => (item.name || "").toLowerCase(),
    },
    {
      field: "quantity",
      header: "Qty",
      align: "center",
      render: (value) => value ?? "",
    },
    {
      field: "purchase_price",
      header: "Cost",
      align: "right",
      render: (value) => {
        if (value === null || value === undefined) return "";
        return `$${parseFloat(value).toFixed(2)}`;
      },
    },
    {
      field: "retail_price",
      header: "Retail",
      align: "right",
      render: (value) => {
        if (value === null || value === undefined) return "";
        return `$${parseFloat(value).toFixed(2)}`;
      },
    },
    {
      field: "list_sale_price",
      header: "Price",
      align: "right",
      render: (value) => {
        if (value === null || value === undefined) return "";
        return `$${parseFloat(value).toFixed(2)}`;
      },
    },
    {
      field: "purchase_date",
      header: "Purchased",
      render: (value) => formatDate(value),
    },
    {
      field: "created_at",
      header: "Added",
      render: (value) => formatDate(value),
    },
    {
      field: "actions",
      header: "Actions",
      width: "120px",
      sortable: false,
      align: "center",
      render: (_, product) => (
        // The row itself navigates to the entity page, so every click in this
        // cell has to stop propagating or the buttons never get a turn.
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (actionsOpen?.id === product.id) {
              setActionsOpen(null);
            } else {
              const rect = e.currentTarget.getBoundingClientRect();
              setActionsOpen({ id: product.id, x: rect.right, y: rect.bottom });
            }
          }}
          className="btn btn-secondary btn-sm"
          title="Listing and review actions"
          style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}
        >
          Actions ▾
        </button>
      ),
    },
  ];

  const actionsProduct =
    actionsOpen && (products || []).find((p) => p.id === actionsOpen.id);

  const actionsMenu = actionsProduct && (
    <>
      {/* Backdrop: swallows the next click anywhere to close the menu */}
      <div
        onClick={() => setActionsOpen(null)}
        style={{ position: "fixed", inset: 0, zIndex: 999 }}
      />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: actionsOpen.y + 4,
          left: Math.max(8, actionsOpen.x - 170),
          zIndex: 1000,
          width: "170px",
          display: "flex",
          flexDirection: "column",
          background: "var(--bg-secondary, var(--bg-primary, #fff))",
          border: "1px solid var(--border-color)",
          borderRadius: "var(--radius-sm)",
          boxShadow: "0 6px 20px rgba(0,0,0,.35)",
          padding: "4px",
        }}
      >
        {[
          { label: "List on OfferUp", act: () => setOfferupProduct(actionsProduct) },
          { label: "List on Facebook", act: () => setFacebookProduct(actionsProduct) },
          { label: "List on eBay", act: () => setEbayProduct(actionsProduct) },
        ].map(({ label, act }) => (
          <button
            key={label}
            onClick={() => {
              setActionsOpen(null);
              act();
            }}
            className="btn btn-ghost btn-sm"
            style={{ justifyContent: "flex-start", textAlign: "left" }}
          >
            {label}
          </button>
        ))}
        {/^[A-Z0-9]{10}$/.test(actionsProduct.sku || "") && (
          <a
            href={`https://www.amazon.com/review/create-review?encoding=UTF&channel=vine-portal&asin=${actionsProduct.sku}#fs-fill`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              setActionsOpen(null);
              markVineReviewed(actionsProduct);
            }}
            className="btn btn-ghost btn-sm"
            title={
              actionsProduct.vine_reviewed_at
                ? `Vine review marked done ${formatDate(actionsProduct.vine_reviewed_at)} — click to reopen the form`
                : "Open the Amazon Vine review form (auto-fills from My Review) and mark this product as reviewed"
            }
            style={{
              justifyContent: "flex-start",
              textAlign: "left",
              ...(actionsProduct.vine_reviewed_at
                ? { color: "var(--success, #00d498)" }
                : {}),
            }}
          >
            {actionsProduct.vine_reviewed_at ? "Vine review ✓" : "Vine review"}
          </a>
        )}
      </div>
    </>
  );

  // Card renderer
  const renderCard = (product) => (
    <div
      className="card"
      style={{
        aspectRatio: "1",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "var(--spacing-md)",
        transition: "transform 0.15s, box-shadow 0.15s",
        overflow: "hidden",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "var(--shadow-lg)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "var(--shadow-sm)";
      }}
    >
      {product.image_path ? (
        <img
          src={media.getImageUrl(product.image_path)}
          alt=""
          style={{
            width: "80px",
            height: "80px",
            objectFit: "contain",
            marginBottom: "var(--spacing-sm)",
            borderRadius: "var(--radius-sm)",
          }}
        />
      ) : (
        <div
          style={{
            marginBottom: "var(--spacing-sm)",
            color: "var(--text-secondary)",
          }}
        >
          <ProductIcon size={40} />
        </div>
      )}
      <div
        style={{
          fontWeight: 600,
          fontSize: "0.875rem",
          marginBottom: "var(--spacing-xs)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}
      >
        {product.name}
      </div>
      {product.list_sale_price != null && (
        <div
          style={{
            color:
              parseFloat(product.list_sale_price) === 0
                ? "var(--success)"
                : "var(--accent-primary)",
            fontWeight: 600,
          }}
        >
          {parseFloat(product.list_sale_price) === 0
            ? "Free"
            : `$${parseFloat(product.list_sale_price).toFixed(2)}`}
        </div>
      )}
      {product.tags && product.tags.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "4px",
            marginTop: "var(--spacing-xs)",
          }}
        >
          {product.tags.slice(0, 2).map((tag) => (
            <span
              key={tag.id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                padding: "2px 6px",
                borderRadius: "var(--radius-sm)",
                background: `${tag.color}20`,
                fontSize: "0.7rem",
              }}
            >
              <span
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  background: tag.color,
                }}
              />
              {tag.name}
            </span>
          ))}
          {product.tags.length > 2 && (
            <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
              +{product.tags.length - 2}
            </span>
          )}
        </div>
      )}
    </div>
  );

  // Bulk actions
  const bulkActions = [
    ...(selectedIds.length >= 2
      ? [
          {
            label: "Merge",
            icon: MergeIcon,
            onClick: () => setShowMergeModal(true),
            variant: "btn-secondary",
          },
        ]
      : []),
    {
      label: exporting ? "Exporting…" : "Export CSV",
      icon: DownloadIcon,
      onClick: handleBulkExportCsv,
      variant: "btn-secondary",
      disabled: exporting,
    },
    {
      label: "Delete",
      icon: TrashIcon,
      onClick: () => setShowDeleteConfirm(true),
      variant: "btn-danger",
    },
  ];

  return (
    <div style={{ minHeight: "100vh", paddingBottom: "80px" }}>
      <div className="header">
        <div className="header-content">
          <button
            onClick={() => navigate("/")}
            className="btn btn-ghost btn-icon"
          >
            <BackIcon size={20} />
          </button>
          <div className="header-text">
            <h1 className="header-title">Products</h1>
            <p className="header-subtitle">Manage your product inventory</p>
          </div>
          <div className="header-actions-right">
            <button
              onClick={() => navigate("/create?type=product")}
              className="btn btn-secondary"
              title="Add new product with all fields"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <PlusIcon size={16} />
              Add Product
            </button>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Stats (hidden in data-saver mode — each card is a full-table scan) */}
        <div className="stats">
          {!LOW_DATA_MODE && (
            <>
              <div className="stat-card">
                <div className="stat-value">{stats.totalCount}</div>
                <div className="stat-label">Total Products</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.inContainers}</div>
                <div className="stat-label">In Containers</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">${stats.totalValue.toFixed(0)}</div>
                <div className="stat-label">Total Value</div>
              </div>
            </>
          )}
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              gap: "var(--spacing-sm)",
            }}
          >
            <button
              onClick={() => setShowVineImportModal(true)}
              className="btn btn-secondary"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <UploadIcon size={16} />
              Import from Vine
            </button>
            <input
              ref={reviewImportInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={(e) => {
                handleImportReviewsFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <button
              onClick={() => reviewImportInputRef.current?.click()}
              className="btn btn-secondary"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
              title="Load a CSV (Id, Review Title, Review Content, Rating) into each product's My Review"
            >
              <UploadIcon size={16} />
              Import Reviews
            </button>
            <button
              onClick={() => setShowDuplicateModal(true)}
              className="btn btn-secondary"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <DuplicateIcon size={16} />
              Find Duplicates
            </button>
          </div>
        </div>

        {/* Marketplace filter */}
        <div
          style={{
            display: "flex",
            gap: "var(--spacing-sm)",
            marginBottom: "var(--spacing-md)",
          }}
        >
          <select
            value={listedFilter}
            onChange={(e) => {
              updateParams({ listed: e.target.value });
              setSelectedIds([]);
            }}
            className="input"
            style={{ width: "auto", minWidth: "150px", margin: 0 }}
          >
            <option value="">All Marketplaces</option>
            <option value="offerup">Listed on OfferUp</option>
            <option value="facebook">Listed on Facebook</option>
            <option value="ebay">Listed on eBay</option>
            <option value="none">Not Listed</option>
          </select>
          <select
            value={reviewPreset}
            onChange={(e) => {
              const p = REVIEW_PRESETS[e.target.value] || {
                revs: "",
                myrev: "",
                vined: "",
              };
              updateParams({ revs: p.revs, myrev: p.myrev, vined: p.vined });
              setSelectedIds([]);
            }}
            className="input"
            style={{ width: "auto", minWidth: "150px", margin: 0 }}
          >
            <option value="">All Review States</option>
            <option value="needs">Needs My Review</option>
            <option value="mine">Has My Review</option>
            <option value="vineready">Ready for Vine</option>
            <option value="vinedone">Vine Review Done</option>
            <option value="none">No Reviews Yet</option>
          </select>
          {boardFilter && (
            <button
              onClick={() => {
                updateParams({ board: "" });
                setSelectedIds([]);
              }}
              className="btn btn-primary"
              title="Showing one Kanban column — click to clear"
            >
              Board: {boardFilter.split(".")[1]?.replace(/_/g, " ")} ✕
            </button>
          )}
        </div>

        {/* Refresh failure banner — the table below may be stale cached data */}
        {productsError && (
          <div
            className="card"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "var(--spacing-md)",
              padding: "var(--spacing-md)",
              marginBottom: "var(--spacing-md)",
              color: "var(--accent-danger)",
            }}
          >
            <span>{productsError}</span>
            <button
              onClick={() => refreshProducts()}
              className="btn btn-secondary btn-sm"
            >
              Retry
            </button>
          </div>
        )}

        {/* DataTable */}
        <DataTable
          data={products || []}
          loading={productsLoading}
          columns={columns}
          searchable
          searchPlaceholder="Search products..."
          searchValue={searchQuery}
          onSearchChange={handleSearchChange}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onSelectAllAcrossPages={handleSelectAllAcrossPages}
          totalItemsCount={
            searchQuery ||
            listedFilter ||
            reviewsFilter ||
            myReviewFilter ||
            vinedFilter ||
            boardFilter
              ? undefined
              : stats.totalCount
          }
          viewToggle
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          renderCard={renderCard}
          onRowClick={(product) => navigate(`/entity/${product.id}`)}
          bulkActions={bulkActions}
          hasMore={productsHasMore}
          loadingMore={productsLoadingMore}
          onLoadMore={loadMoreProducts}
          emptyIcon={ProductIcon}
          emptyTitle="No products found"
          emptySubtitle="Start by adding your first product!"
        />
        {actionsMenu}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleBulkDelete}
        title={`Delete ${selectedIds.length} item${selectedIds.length > 1 ? "s" : ""}?`}
        message={
          <>
            <p>
              Are you sure you want to move {selectedIds.length} item
              {selectedIds.length > 1 ? "s" : ""} to trash?
            </p>
            <p className="text-muted" style={{ fontSize: "0.875rem" }}>
              You can restore them later from the Trash.
            </p>
          </>
        }
        confirmText="Delete"
        confirmVariant="danger"
      />

      {/* Merge Modal */}
      {showMergeModal && (
        <div className="modal-overlay" onClick={() => setShowMergeModal(false)}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "500px" }}
          >
            <div className="modal-header">
              <h2>Merge {selectedIds.length} Items</h2>
              <button
                onClick={() => setShowMergeModal(false)}
                className="btn btn-ghost btn-icon"
              >
                <CloseIcon size={18} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: "var(--spacing-md)" }}>
                Select which item to keep as the primary. All data from other
                items will be merged into it.
              </p>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--spacing-sm)",
                }}
              >
                {getSelectedProducts().map((product) => (
                  <div
                    key={product.id}
                    onClick={() => !merging && handleMerge(product.id)}
                    className="card"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--spacing-md)",
                      padding: "var(--spacing-md)",
                      cursor: merging ? "not-allowed" : "pointer",
                      opacity: merging ? 0.6 : 1,
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      if (!merging)
                        e.currentTarget.style.background = "var(--bg-tertiary)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "";
                    }}
                  >
                    {product.image_path ? (
                      <img
                        src={media.getImageUrl(product.image_path)}
                        alt=""
                        style={{
                          width: "50px",
                          height: "50px",
                          objectFit: "cover",
                          borderRadius: "var(--radius-sm)",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "var(--bg-secondary)",
                          borderRadius: "var(--radius-sm)",
                          color: "var(--text-muted)",
                        }}
                      >
                        <ProductIcon size={24} />
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{product.name}</div>
                      <div
                        style={{
                          fontSize: "0.875rem",
                          color: "var(--text-muted)",
                        }}
                      >
                        {product.purchase_price != null &&
                          `Cost: $${parseFloat(product.purchase_price).toFixed(2)}`}
                        {product.purchase_price != null &&
                          product.list_sale_price != null &&
                          " · "}
                        {product.list_sale_price != null &&
                          `Price: $${parseFloat(product.list_sale_price).toFixed(2)}`}
                      </div>
                    </div>
                    <div
                      style={{
                        color: "var(--accent-primary)",
                        fontWeight: 500,
                      }}
                    >
                      Keep this →
                    </div>
                  </div>
                ))}
              </div>
              {merging && (
                <div
                  style={{
                    textAlign: "center",
                    marginTop: "var(--spacing-md)",
                    color: "var(--text-muted)",
                  }}
                >
                  Merging...
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowMergeModal(false)}
                className="btn btn-secondary"
                disabled={merging}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Scanner Modal */}
      <DuplicateScannerModal
        isOpen={showDuplicateModal}
        onClose={() => setShowDuplicateModal(false)}
      />

      {/* Vine Import Modal */}
      <VineImportModal
        isOpen={showVineImportModal}
        onClose={() => setShowVineImportModal(false)}
        onComplete={() => {
          refreshProducts();
          loadStats();
        }}
      />

      {/* OfferUp Listing Modal */}
      {offerupProduct && (
        <OfferUpListingModal
          product={offerupProduct}
          onClose={() => setOfferupProduct(null)}
          onSuccess={() => {
            refreshProducts();
          }}
        />
      )}

      {/* Facebook Listing Modal */}
      {facebookProduct && (
        <FacebookListingModal
          product={facebookProduct}
          onClose={() => setFacebookProduct(null)}
          onSuccess={() => {
            refreshProducts();
          }}
        />
      )}

      {/* eBay Listing Modal */}
      {ebayProduct && (
        <EbayListingModal
          product={ebayProduct}
          imageUrl={
            ebayProduct.image_path
              ? media.getImageUrl(ebayProduct.image_path)
              : null
          }
          onClose={() => setEbayProduct(null)}
          onSuccess={() => {
            refreshProducts();
          }}
        />
      )}
    </div>
  );
}

export default Products;
